import { Glacier2 } from "ice";
import { delay } from "redux-saga";
import { call, put, take, race, all } from "redux-saga/effects";
import { bindAsyncAction } from "typescript-fsa-redux-saga";
import assertNever from "assert-never";
import { bindProxy, logError, getGanymedeModule, getAuthExceptionsModule, } from "./utils";
import { login, reconnect, reconnectScheduled, tokenRefreshed, session, } from "./actions";
function createSession(payload, router) {
    let sessionPromise;
    const { AccessTokenContextKey, DomainContextKey, UserSessionPrx, } = getGanymedeModule();
    if (payload.type === 'token') {
        const context = new Map();
        context.set(AccessTokenContextKey, payload.token);
        sessionPromise = router.createSession('', '', context);
    }
    else if (payload.type === 'credentials') {
        const { username, password, domain } = payload;
        const context = new Map();
        context.set(DomainContextKey, domain);
        sessionPromise = router.createSession(username, password, context);
    }
    else {
        sessionPromise = assertNever(payload);
    }
    return sessionPromise
        .then(session => bindProxy(UserSessionPrx.uncheckedCast(session)));
}
function createAdapter(router) {
    const communicator = router.ice_getCommunicator();
    return communicator.createObjectAdapterWithRouter('', router);
}
function* connect(helper, payload, options) {
    const router = yield call(helper.getRouter);
    const session = yield call(createSession, payload, router);
    const [token, category, sessionTimeout, adapter] = yield all([
        call(session.getToken),
        call(router.getCategoryForClient),
        call(router.getSessionTimeout),
        call(createAdapter, router),
    ]);
    const sessionData = {
        router, session, token, category, adapter,
        sessionTimeout: sessionTimeout.toNumber(),
    };
    yield call(helper.setSessionData, sessionData);
    if (options.beforeSession) {
        try {
            yield call(options.beforeSession, token);
        }
        catch (e) {
            yield call(helper.destroySession);
            throw e;
        }
    }
    return { token };
}
const loginWorker = bindAsyncAction(login)((payload, helper, options) => connect(helper, Object.assign({ type: 'credentials' }, payload), options));
function getReconnectSeconds(attempt) {
    if (attempt <= 0) {
        return 5;
    }
    else if (attempt === 1) {
        return 15;
    }
    else if (attempt === 2) {
        return 30;
    }
    else {
        return 60;
    }
}
function getReconnectTimestamp(reconnectSeconds) {
    return Math.floor(Date.now() / 1000) + reconnectSeconds;
}
/**
 * Reconnect using token, retry automatically on fail.
 * Throws only when token is invalid.
 */
const reconnectWorker = bindAsyncAction(reconnect)(function* reconnectWorker({ token }, helper, options) {
    for (let attempt = 0;; attempt += 1) {
        const payload = { type: 'token', token };
        try {
            return yield call(connect, helper, payload, options);
        }
        catch (e) {
            if (e instanceof Glacier2.PermissionDeniedException) {
                // only throw when token is invalid
                if (e.reason !== 'internal server error') {
                    yield call(helper.removeToken);
                    throw e;
                }
            }
            logError('Failed to connect', e);
            const reconnectSeconds = getReconnectSeconds(attempt);
            yield put(reconnectScheduled({
                reconnectAt: yield call(getReconnectTimestamp, reconnectSeconds),
            }));
            yield race({
                delay: call(delay, reconnectSeconds * 1000),
                manual: take(helper.reconnectRequestChannel),
            });
        }
    }
});
function getTokenRefreshDelay(expiresAt) {
    const expiresInMs = expiresAt.toNumber() * 1000 - Date.now();
    return Math.max(0, Math.floor(expiresInMs * 0.5));
}
function* refreshTokenLoop(helper) {
    while (true) {
        const session = yield call(helper.getSession);
        const expiresAt = yield call(session.getExpiresAt);
        const refreshDelay = yield call(getTokenRefreshDelay, expiresAt);
        yield call(delay, refreshDelay);
        const token = yield call(session.refreshToken);
        yield call(helper.setToken, token);
        yield put(tokenRefreshed({ token }));
    }
}
const sessionWorker = bindAsyncAction(session)(function* sessionWorker(params, helper) {
    try {
        const { logout } = yield race({
            refresh: call(refreshTokenLoop, helper),
            disconnect: call(helper.waitForConnectionClose),
            logout: take(helper.logoutRequestChannel),
        });
        if (logout) {
            yield call(helper.logout);
            return 'logout';
        }
        throw new Error('disconnected');
    }
    catch (e) {
        const { InvalidToken } = getAuthExceptionsModule();
        if (e instanceof InvalidToken) {
            yield call(helper.destroySession);
            return 'expired';
        }
        throw e;
    }
});
/** @internal */
export default function* iceClientSaga(options, helper) {
    while (true) {
        const token = yield call(helper.getToken);
        let loginResult;
        // todo: catch and log errors from connection to glacier
        try {
            if (token == null) {
                const payload = yield take(helper.loginRequestChannel);
                loginResult = yield call(loginWorker, payload, helper, options);
            }
            else {
                loginResult = yield call(reconnectWorker, { token }, helper, options);
            }
        }
        catch (e) {
            if (e instanceof Glacier2.PermissionDeniedException) {
                console.info("Failed to create session:", e.toString());
            }
            else {
                logError("Failed to create session", e);
            }
            continue;
        }
        try {
            yield call(sessionWorker, undefined, helper);
        }
        catch (e) {
            logError('Session error', e);
        }
        finally {
            yield call(helper.resetRouter);
        }
    }
}



//////////////////
// WEBPACK FOOTER
// ./~/@neolab/ice-client/es/saga.js
// module id = 463
// module chunks = 0